import React from 'react';
import useReport from '../lib/useReport';
import { useFilters } from '../contexts/FilterContext';
import { money, int, pct, niceDate } from '../lib/format';
import { Card, Spinner, ErrorBox, Empty, DataTable } from '../components/ui';

// Store benchmarking: every store ranked against the whole chain on a KPI
// battery, shown as percentile pills. Owners see their stores' ranks plus
// anonymous chain quartiles — never other stores' names or numbers.
const fmtValue = {
  netSales: v => money(v, { cents: false }),
  comp: v => `${v > 0 ? '+' : ''}${(v * 100).toFixed(1)}%`,
  aov: v => money(v),
  salesPerLaborHour: v => money(v, { cents: false }),
  onlineShare: v => pct(v, 0),
  tipsPct: v => pct(v, 1),
  refundRate: v => pct(v, 2),
};

function PctPill({ p }) {
  if (p == null) return <span style={{ color: 'var(--ink-muted)' }}>—</span>;
  const bg = p >= 75 ? 'rgba(12,163,12,0.14)' : p < 25 ? 'rgba(208,59,59,0.12)' : 'var(--page)';
  const color = p >= 75 ? 'var(--good)' : p < 25 ? 'var(--bad)' : 'var(--ink-2)';
  return (
    <span style={{
      display: 'inline-block', minWidth: 34, textAlign: 'center',
      padding: '1px 6px', borderRadius: 999, fontSize: 11, fontWeight: 700,
      background: bg, color, border: '1px solid var(--border)',
    }}>
      P{p}
    </span>
  );
}

const kpiCell = (key) => (r) => {
  const v = r.values[key];
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      {v == null ? '—' : fmtValue[key](v)}{' '}
      <PctPill p={r.percentiles[key]} />
    </span>
  );
};

export default function BenchmarkingPage() {
  const { startDate, endDate, me } = useFilters();
  // Deliberately no locations param: ranking is always against the whole
  // chain; the server scopes which ROWS an owner may see.
  const { data, loading, error } = useReport('benchmarking', { startDate, endDate });

  if (error) return <ErrorBox message={error} />;
  if (loading || !data) return <Spinner label="Ranking the chain…" />;
  if (!data.stores || data.stores.length === 0) return <Empty>No store data for this period.</Empty>;

  const { stores, chainStats, storeCount } = data;
  const isOwner = me?.role === 'owner';
  const medianOf = (key) => {
    const m = chainStats[key]?.median;
    return m == null ? '—' : fmtValue[key](m);
  };

  const kpiCol = (key, label) => ({
    key, label, num: true,
    sortValue: r => r.percentiles[key] ?? -1,
    render: kpiCell(key),
    csv: r => r.values[key] ?? '',
    footer: () => <span title="chain median">{medianOf(key)}</span>,
  });

  return (
    <div className="grid" style={{ gap: 14 }}>
      <Card
        tour="bm-table"
        title={isOwner ? 'Your Stores vs the Chain' : 'Store Scorecard'}
        sub={`${niceDate(startDate)} – ${niceDate(endDate)} · every store ranked against all ${int(storeCount)} stores · P75+ = top quartile, P25− = bottom quartile · footer row = chain median`}
      >
        <DataTable
          csvName="benchmarking.csv"
          defaultSort={{ key: 'overall', dir: 'desc' }}
          columns={[
            { key: 'name', label: 'Store', footer: () => 'Chain median' },
            {
              key: 'overall', label: 'Overall', num: true,
              sortValue: r => r.overall ?? -1,
              render: r => <PctPill p={r.overall} />,
              csv: r => r.overall ?? '',
            },
            kpiCol('comp', 'Comp % (YoY)'),
            kpiCol('aov', 'AOV'),
            kpiCol('salesPerLaborHour', '$ / Labor Hr'),
            kpiCol('onlineShare', 'Online %'),
            kpiCol('tipsPct', 'Tips %'),
            kpiCol('refundRate', 'Refund %'),
            kpiCol('netSales', 'Net Sales'),
          ]}
          rows={stores.map(r => ({ ...r, __key: r.locationId }))}
          footer
        />
        <div className="legend-note">
          Percentile = share of the chain this store beats on that measure ({'higher is better; refund % is inverted'}).
          “Overall” averages the performance percentiles — net sales is volume context and excluded.
          Comp % compares the same weekday-aligned window last year. Stores without shift punches show “—” for $/labor hr and rank only on the rest.
        </div>
      </Card>

      {isOwner && (
        <Card title="How to read this" sub="your rank is computed against every store in the chain">
          <div style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.7 }}>
            <PctPill p={82} /> means the store outperforms 82% of the chain on that measure for this period.
            The footer row shows the chain median for each measure — other stores' individual numbers aren't shown.
          </div>
        </Card>
      )}
    </div>
  );
}
